import React, { useCallback, useMemo } from 'react';
import c from 'classnames';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ImageOutline } from '@lyearn/icons';
import { useAddCoverDialogContext } from '@lyearn/lygo-core';
import { MenuListItems } from '@lyearn/molecules';
import { Box, Popover, Tooltip } from '@lyearn/ui';

import { colorStyles, iconHoverStyles, iconStyles, menuListWidth } from './styles';
import getImageMenuItems, { ImageMenuActions } from './utils';

const AddImageButton: React.FC<{
  backgroundImgSrc?: string;
  onImageChange: (newImage: string | null) => void;
  iconColor: string;
  iconHoverColor: string;
}> = (props) => {
  const { backgroundImgSrc, onImageChange, iconColor, iconHoverColor } = props;
  const [open, { toggle }] = useToggle();
  const { t } = useTranslation();
  const { openDialog } = useAddCoverDialogContext();
  const items = useMemo(() => getImageMenuItems(t), [t]);

  const onSubmitCallback = useCallback(
    (cover: { url: string }) => {
      onImageChange(cover.url);
    },
    [onImageChange],
  );

  const openAddCoverDialog = useCallback(() => {
    openDialog?.(onSubmitCallback, { url: backgroundImgSrc });
  }, [backgroundImgSrc, onSubmitCallback, openDialog]);

  const onAction = useCallback(
    (action: string) => {
      switch (action) {
        case ImageMenuActions.Remove:
          onImageChange(null);
          break;
        case ImageMenuActions.Replace:
          openAddCoverDialog();
          break;
      }
      toggle();
    },
    [onImageChange, openAddCoverDialog, toggle],
  );

  return (
    <Popover open={open} onOpenChange={toggle}>
      <Popover.Button>
        <Tooltip placement="top" title={t('Lygo:FlashCard.ImageTooltip')!}>
          <Box
            className={c(
              iconStyles(!!backgroundImgSrc),
              iconHoverStyles(iconHoverColor),
              'flex h-40 w-40 items-center justify-center rounded-8 ms-4',
            )}
            role="button"
            onClick={backgroundImgSrc ? toggle : openAddCoverDialog}>
            <ImageOutline className={c('h-24 w-24', colorStyles(iconColor))} />
          </Box>
        </Tooltip>
      </Popover.Button>
      <Popover.Content className={menuListWidth} position="bottom-start" renderAs="popper">
        <MenuListItems className="p-8" items={items} onAction={onAction} />
      </Popover.Content>
    </Popover>
  );
};

export default AddImageButton;
